import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Flame, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AllRounderLogo from "@/components/AllRounderLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BbqAdminApprovePage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [state, setState] = useState({ loading: true, error: "", rsvp: null });
  const [submitting, setSubmitting] = useState(false);
  const [approved, setApproved] = useState(false);

  useEffect(() => {
    document.title = "BBQ · Admin approve";
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/bbq-rsvps/${id}`, { params: { token } });
        if (cancel) return;
        setState({ loading: false, error: "", rsvp: r.data });
        setApproved(r.data.approval_status === "approved");
      } catch (err) {
        if (cancel) return;
        const detail =
          err?.response?.data?.detail ||
          err?.response?.status === 403
            ? "Invalid or missing token"
            : "RSVP not found";
        setState({ loading: false, error: String(detail), rsvp: null });
      }
    })();
    return () => {
      cancel = true;
    };
  }, [id, token]);

  const approve = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/bbq-rsvps/${id}/approve`,
        {},
        { params: { token } }
      );
      setApproved(true);
      setState((s) => ({ ...s, rsvp: r.data.rsvp }));
      toast.success("RSVP approved 🎉", {
        description: "Customer confirmation email has been sent.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Approval failed";
      toast.error(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="bbq-admin-page"
      className="min-h-screen bg-[#0A0806] text-white"
      style={{ fontFamily: "'Noto Sans KR', 'Fraunces', serif" }}
    >
      <nav className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <Link
            to="/bbq"
            className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm font-semibold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to BBQ page
          </Link>
          <AllRounderLogo variant="horizontal" color="#FAFAFA" />
        </div>
      </nav>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E85A2C]">
          // Admin Approval
        </p>
        <h1
          className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight leading-[0.95]"
          style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
        >
          Approve this RSVP.
        </h1>

        {state.loading && (
          <div className="mt-10 flex items-center gap-3 text-white/60">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading RSVP…
          </div>
        )}

        {!state.loading && state.error && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 rounded-md border border-red-500/30 bg-red-500/10 p-6 flex items-start gap-3"
          >
            <ShieldAlert className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div>
              <div className="font-bold text-white">Can't open this RSVP</div>
              <div className="text-sm text-white/60 mt-1">{state.error}</div>
              <div className="text-xs text-white/40 mt-3">
                Make sure you opened this link from the admin email — it
                includes the correct token.
              </div>
            </div>
          </motion.div>
        )}

        {state.rsvp && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
            className="mt-10 rounded-md border border-white/10 bg-[#0F0B08]"
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div
                    className="text-white text-3xl sm:text-4xl font-bold tracking-tight"
                    style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
                  >
                    {state.rsvp.name}
                  </div>
                  <div className="mt-1 text-white/60 text-sm">
                    {state.rsvp.email} · {state.rsvp.phone}
                  </div>
                </div>
                <span
                  data-testid="bbq-admin-status"
                  className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.22em] ${
                    approved
                      ? "bg-[#E85A2C] text-white"
                      : "bg-white/10 text-white/70"
                  }`}
                >
                  {approved ? "Approved" : "Pending"}
                </span>
              </div>

              <div className="mt-6 divide-y divide-white/5 border-y border-white/10">
                <Row label="Group Size" value={`${state.rsvp.group_size} pax`} />
                <Row label="Package" value={`${state.rsvp.status} · ${state.rsvp.package}`} />
                <Row label="Social" value={state.rsvp.social || "-"} />
                <Row
                  label="Total"
                  value={
                    <span
                      className="text-[#E85A2C] text-2xl font-bold tabular"
                      style={{ fontFamily: "'Fraunces', serif", fontWeight: 700 }}
                    >
                      ${state.rsvp.total} AUD
                    </span>
                  }
                />
                <Row
                  label="Reference"
                  value={
                    <span className="font-mono text-white/80">
                      BBQ {state.rsvp.name}
                    </span>
                  }
                />
                <Row
                  label="Submitted"
                  value={new Date(state.rsvp.created_at).toLocaleString()}
                />
              </div>

              <div className="mt-8">
                {approved ? (
                  <div className="rounded-sm border border-[#E85A2C]/40 bg-[#E85A2C]/[0.06] p-5 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#E85A2C] flex items-center justify-center shrink-0">
                      <Flame className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-white font-bold">
                        Confirmation sent to {state.rsvp.email}
                      </div>
                      <div className="text-white/60 text-sm mt-1">
                        Approved at{" "}
                        {state.rsvp.approved_at
                          ? new Date(state.rsvp.approved_at).toLocaleString()
                          : "just now"}
                        . Nothing else for you to do.
                      </div>
                    </div>
                  </div>
                ) : (
                  <Button
                    data-testid="bbq-admin-approve-btn"
                    onClick={approve}
                    disabled={submitting}
                    className="w-full h-14 rounded-sm bg-[#E85A2C] hover:bg-[#c94a20] text-white font-bold text-base"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Approving…
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Approve & send confirmation email
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </section>
    </main>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-4">
      <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">
        {label}
      </div>
      <div className="text-white text-sm font-bold text-right">{value}</div>
    </div>
  );
}
